class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Clear any stale inline visibility left by legacy scripts so the
    // class-based open/close below always wins.
    this.style.removeProperty('visibility');

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.style.removeProperty('visibility');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;

    if (parsedState.sections) {
      this.getSectionsToRender().forEach((section => {
        const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (!sectionElement || !parsedState.sections[section.id]) return;
        sectionElement.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }));
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });

    // Re-init the upsell carousel inside the freshly rendered drawer
    // (guarded: requires jQuery + slick, skips already-initialized carousels).
    if (typeof updateslick === 'function') updateslick();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/*
 * Global helper: add a variant to the cart, refresh the drawer + header
 * cart-count bubble via Shopify's Section Rendering API, then open the drawer.
 * Any custom add-to-cart button in the theme can call this instead of
 * hand-rolling its own XHR / DOM patching:
 *
 *   window.themeAddToCart(variantId, quantity, { onSuccess, onError, openDrawer })
 *
 * Pass openDrawer: false to refresh the drawer contents + bubble silently
 * (e.g. while another modal is on top) without opening the drawer.
 */
window.themeAddToCart = function (variantId, quantity, callbacks) {
  callbacks = callbacks || {};
  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  const config = (typeof fetchConfig === 'function')
    ? fetchConfig('javascript')
    : { method: 'POST', headers: { 'Accept': 'application/javascript' } };
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', quantity || 1);

  if (cart && typeof cart.getSectionsToRender === 'function') {
    formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
  }
  config.body = formData;

  const addUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';

  return fetch(addUrl, config)
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        // Shopify error payload (sold out, limits, etc.)
        if (callbacks.onError) callbacks.onError(response);
        else console.error('Add to cart failed:', response.description || response.message);
        return response;
      }
      if (cart && typeof cart.renderContents === 'function') {
        if (callbacks.openDrawer === false && response.sections && typeof cart.getSectionsToRender === 'function') {
          // Silent refresh: update drawer + bubble sections without opening.
          const drawerInner = cart.querySelector && cart.querySelector('.drawer__inner');
          if (drawerInner && drawerInner.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
          cart.classList.remove('is-empty');
          cart.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            if (!sectionElement || !response.sections[section.id]) return;
            sectionElement.innerHTML = cart.getSectionInnerHTML(response.sections[section.id], section.selector);
          });
          const overlay = cart.querySelector && cart.querySelector('#CartDrawer-Overlay');
          if (overlay && typeof cart.close === 'function') overlay.addEventListener('click', cart.close.bind(cart));
          if (typeof updateslick === 'function') updateslick();
        } else {
          cart.renderContents(response);
        }
      } else {
        window.location = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
      }
      if (callbacks.onSuccess) callbacks.onSuccess(response);
      return response;
    })
    .catch((e) => {
      console.error(e);
      if (callbacks.onError) callbacks.onError(e);
    });
};